/**
 * Pure derivation helpers for the Class View page hero. Extracted from
 * ClassDetailPage.tsx so unit tests can load them without pulling in
 * React or MUI.
 */

/**
 * Title-case a database-stored lowercase value ("regular" → "Regular",
 * "created class" → "Created Class"). Preserves an empty input as an
 * empty string so callers can chain a fallback (`capitalise(v) || "Not
 * Set"`). Intentionally simple: no locale-aware casing, no small-word
 * exceptions — the values we render here are single-word or short
 * multi-word enums.
 */
export const capitalise = (raw?: string | null): string => {
  if (raw === null || raw === undefined) return "";
  const cleaned = String(raw).trim();
  if (!cleaned) return "";
  return cleaned
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
};

/**
 * "Monday and Wednesday" · "Monday, Wednesday and Friday" · "" when
 * no schedule days are configured. Used in the hero subtitle so the
 * days list reads like prose rather than "monday, wednesday".
 */
export const formatDaysList = (days?: string[] | null): string => {
  const list = (days || [])
    .map((d) => (d ? d.charAt(0).toUpperCase() + d.slice(1).toLowerCase() : ""))
    .filter(Boolean);
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
};

/**
 * "Grade 10 · Maths, Science, ICT" — the header subtitle line built
 * from the grouped `grade_subjects` payload. Multi-grade classes join
 * groups with " / " so nothing is misleadingly flattened. Falls back
 * to the flat `subjects` / `grades` shape for callers still on the
 * old payload.
 */
export const gradeSubjectsSummaryLine = (
  gradeSubjects?: Array<{ grade?: string; subjects?: Array<{ name?: string } | string> }> | null,
  cls?: { grades?: string[]; subjects?: string[] },
): string => {
  const groups = Array.isArray(gradeSubjects) ? gradeSubjects : [];
  const parts: string[] = [];
  groups.forEach((g) => {
    if (!g) return;
    const gradeName = g.grade ? String(g.grade) : "";
    const subNames = (g.subjects || [])
      .map((s: any) => (typeof s === "string" ? s : s?.name))
      .filter(Boolean) as string[];
    if (!gradeName && subNames.length === 0) return;
    parts.push([gradeName, subNames.join(", ")].filter(Boolean).join(" · "));
  });
  if (parts.length > 0) return parts.join(" / ");
  // Fallback for callers on the flat payload.
  const grade = cls?.grades?.[0] || "";
  const subs = (cls?.subjects || []).join(", ");
  return [grade, subs].filter(Boolean).join(" · ");
};
